import type {QueryClient} from "@tanstack/react-query";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {uniqueUuid} from "@vesta/unique/unique";
import {z as zod} from "zod";
import {adminUserDetailValidator, type AdminUserDetail} from "~/domainModels/adminModels/adminUserDetails";
import {USER_DETAILS_QUERY_KEY} from "../user-profiles/route";
import {
    adgptTokenBalanceValidator,
    type AdgptTokenBalance,
    adgptTokenLedgerEntryValidator,
    type AdgptTokenLedgerEntry,
} from "./-types";

export const ADGPT_TOKEN_BALANCES_QUERY_KEY = uniqueUuid`76a2e9ba-c8c4-4479-b64d-30a85d271ace`;
export const ADGPT_TOKEN_LEDGER_QUERY_KEY = uniqueUuid`aa68a7c8-c30a-4ba4-9959-1e5778a25b3b`;
export const ADGPT_TOKEN_USAGE_LEADERBOARD_QUERY_KEY = uniqueUuid`32a87c85-7d72-4683-a281-f577044575fa`;

const PAGE_SIZE = 200;

export async function getTokenLedger({
    accessToken,
    plutoBaseUrl,
    userId,
}: {
    accessToken: string;
    plutoBaseUrl: string;
    userId?: AdgptTokenBalance["userId"];
}): Promise<Array<AdgptTokenLedgerEntry>> {
    const entries: Array<AdgptTokenLedgerEntry> = [];
    let offset = 0;
    let page: Array<AdgptTokenLedgerEntry>;

    do {
        const result = await robustJsonResultClientFetch(
            `${plutoBaseUrl}/admin/adgpt/tokens/ledger/get-all`,
            {
                body: JSON.stringify({limit: PAGE_SIZE, offset: offset, ...(userId === undefined ? {} : {userId: userId})}),
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                method: "POST",
            },
            zod.array(adgptTokenLedgerEntryValidator),
        );
        if (result.success !== true) {
            throw new Error("Failed to fetch the AI credits ledger", {cause: result.error});
        }

        page = result.data;
        entries.push(...page);
        offset += PAGE_SIZE;
    } while (page.length === PAGE_SIZE);

    return entries;
}

export async function getAllTokenBalances({
    accessToken,
    plutoBaseUrl,
    queryClient,
}: {
    accessToken: string;
    plutoBaseUrl: string;
    queryClient: QueryClient;
}): Promise<Array<AdgptTokenBalance>> {
    return await queryClient.ensureQueryData({
        queryKey: [ADGPT_TOKEN_BALANCES_QUERY_KEY],
        queryFn: async () => {
            const balances: Array<AdgptTokenBalance> = [];
            let offset = 0;
            let page: Array<AdgptTokenBalance>;

            do {
                const result = await robustJsonResultClientFetch(
                    `${plutoBaseUrl}/admin/adgpt/tokens/balances/get-all`,
                    {
                        body: JSON.stringify({limit: PAGE_SIZE, offset: offset}),
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            "Content-Type": "application/json",
                        },
                        method: "POST",
                    },
                    zod.array(adgptTokenBalanceValidator),
                );
                if (result.success !== true) {
                    throw new Error("Failed to fetch AdGPT token balances", {cause: result.error});
                }

                page = result.data;
                balances.push(...page);
                offset += PAGE_SIZE;
            } while (page.length === PAGE_SIZE);

            return balances;
        },
    });
}

export async function getPlatformUserDetails({
    accessToken,
    plutoBaseUrl,
    queryClient,
}: {
    accessToken: string;
    plutoBaseUrl: string;
    queryClient: QueryClient;
}): Promise<Array<AdminUserDetail>> {
    return await queryClient.ensureQueryData({
        queryKey: [USER_DETAILS_QUERY_KEY],
        queryFn: async () => {
            const result = await robustJsonResultClientFetch(
                `${plutoBaseUrl}/admin/user-details/get`,
                {
                    body: JSON.stringify({}),
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    method: "POST",
                },
                zod.array(adminUserDetailValidator),
            );
            if (result.success !== true) {
                throw new Error("Failed to fetch platform user details", {cause: result.error});
            }
            return result.data;
        },
    });
}
